<template>
	<div v-if="product.discount && formattedDiscountPrice" class="product-price product-price--action">
		<span class="price" data-price>
			{{ formattedNormalPrice }}
		</span>
		<strong class="price-action" data-action="price">
			{{ formattedDiscountPrice }}
			<span v-if="isMonthlyPrice" class="price-per-period">/mnd</span>
		</strong>
		<em class="price-after-period caption" data-action="description">
			{{ formattedPeriodText }}
		</em>
	</div>

	<div v-else class="product-price">
		<span class="price" data-price>
			{{ formattedNormalPrice }}
			<span v-if="isMonthlyPrice" class="price-per-period">/mnd</span>
			<span v-if="isOncePrice" class="price-per-period"> eenmalig</span>
		</span>
	</div>
</template>

<script lang="ts" setup>
import type {Product} from '@/types/product';
import {computed} from 'vue';
import {getFormattedPrice} from '@/utils/priceFormatter';
import {getFormattedActionPeriod} from '@/utils/textFormatter';

const props = defineProps<{
		product: Product
	}>();

	const getOrderAmount = computed(() => props.product.incrementAmount ?? 1);

	const formattedPeriodText = computed(() => getFormattedActionPeriod(props.product));

	const formattedNormalPrice = computed(() => {
		const price = getOrderAmount.value > 1 ? getOrderAmount.value * props.product.price : props.product.price;
		return getFormattedPrice(price);
	});

	const formattedDiscountPrice = computed(() => {
		if (props.product.discount === null) {
			return null;
		}

		return getFormattedPrice(getOrderAmount.value * props.product.discount.price);
	});

	const isMonthlyPrice = computed(() => props.product.priceType === 'MONTHLY');
	const isOncePrice = computed(() => props.product.priceType === 'ONCE');

</script>

<style lang="scss" scoped>
	.product-price {
		display: flex;
		flex-direction: column;
		justify-items: flex-start;
		align-items: var(--product-price__align-items, end);

		& > * {
			font-weight: normal;
			font-style: normal;
			white-space: nowrap;
			flex: 0 0 auto;
		}
	}

	.price:not(:only-child) {
		text-decoration: line-through;
	}

	.price-action {
		color: var(--color-discount);
	}

	.price-after-period.caption {
		line-height: inherit;
	}
</style>
